/**
 * MongoDB implementation of Team Repository
 * 
 * Clean infrastructure implementation
 */
import { MongoClient, Db, Collection } from 'mongodb';
import { Team } from '../../bounded-contexts/parties/domain/aggregates/Team';
import { ITeamRepository } from '../../bounded-contexts/parties/domain/repositories/ITeamRepository';
import { v4 as uuidv4 } from 'uuid';

export class MongoTeamRepository implements ITeamRepository {
  private db: Db;
  private collection: Collection;
  private client: MongoClient;

  constructor(
    private readonly mongoUrl: string,
    private readonly dbName: string
  ) {}

  private async ensureConnection(): Promise<void> {
    if (!this.client) {
      this.client = new MongoClient(this.mongoUrl);
      await this.client.connect();
      this.db = this.client.db(this.dbName);
      this.collection = this.db.collection('teams');
      
      // Create indexes
      await this.collection.createIndex({ organizationId: 1 });
      await this.collection.createIndex({ leaderId: 1 });
      await this.collection.createIndex({ 'members.personId': 1 });
      await this.collection.createIndex({ isActive: 1 });
      await this.collection.createIndex({ name: 'text' });
    }
  }

  async save(team: Team): Promise<void> {
    await this.ensureConnection();
    const snapshot = team.toSnapshot();
    
    await this.collection.replaceOne(
      { _id: snapshot.id },
      { _id: snapshot.id, ...snapshot },
      { upsert: true }
    );
  }

  async findById(id: string): Promise<Team | null> {
    await this.ensureConnection();
    const doc = await this.collection.findOne({ _id: id });
    
    if (!doc) return null;
    
    return Team.fromSnapshot(doc);
  }

  async findByOrganization(organizationId: string): Promise<Team[]> {
    await this.ensureConnection();
    const docs = await this.collection.find({ 
      organizationId,
      isActive: true 
    }).toArray();
    
    return docs.map(doc => Team.fromSnapshot(doc));
  }

  async findByMember(personId: string): Promise<Team[]> {
    await this.ensureConnection();
    const docs = await this.collection.find({ 
      'members.personId': personId,
      isActive: true 
    }).toArray();
    
    return docs.map(doc => Team.fromSnapshot(doc));
  }

  async findByLeader(leaderId: string): Promise<Team[]> {
    await this.ensureConnection();
    const docs = await this.collection.find({ 
      leaderId,
      isActive: true 
    }).toArray();
    
    return docs.map(doc => Team.fromSnapshot(doc));
  }

  async findActive(): Promise<Team[]> {
    await this.ensureConnection();
    const docs = await this.collection.find({ 
      isActive: true 
    }).toArray();
    
    return docs.map(doc => Team.fromSnapshot(doc));
  }

  async remove(id: string): Promise<void> {
    await this.ensureConnection();
    // Soft delete - mark as disbanded
    await this.collection.updateOne(
      { _id: id },
      { 
        $set: { 
          isActive: false, 
          disbandedAt: new Date(),
          updatedAt: new Date() 
        } 
      }
    );
  }

  nextIdentity(): string {
    return uuidv4();
  }

  async disconnect(): Promise<void> {
    if (this.client) {
      await this.client.close();
    }
  }
}